/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import React, { useEffect } from 'react'
import LanguageStrings from '../../../../i18n/locales'
import CloseIcon from '@mui/icons-material/Close'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import { TextField, Typography, FormHelperText, CircularProgress, DialogTitle, Dialog, DialogContent, DialogActions } from '@mui/material'
import { connect, type ConnectedProps, useSelector } from 'react-redux'
import { type RootState } from '../../../../store'
import { isValidDeviceType, isValidName } from '../../../../utils/validator'
import { Button } from '../../../../components/Button'
import { GenXDropdown, type Option } from '../../../../components/GenXDropdown'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import WarningIcon from '@mui/icons-material/WarningAmber'
import { getSampleDeviceOnboardingFileUrl } from '../../../../services/fileDownloadService'
import errorCrossIcon from '../../../../assets/errorCrossIcon.svg'
import { createCustomeDeviceTemplateService, fetchDeviceList, getCustomDeviceDropdownMetadata } from '../../redux/actionCreators'
import { type UploadFileInfoState } from '../../redux/createDeviceInfoSlice'
import { type DeviceDropDownMetadataState } from '../../redux/getDeviceDropDownMetaDataSlice'
import ModelPopup from '../../../../components/Modal'
import './AddDevice.css'

const AddDeviceStrings = LanguageStrings().CustomDeviceCollectionStrings.AddDeviceComponent
const DEVICE_TYPE_MAX_LENGTH = 20

const textStyle = {
  root: {
    backgroundColor: '#272727',
    '& .MuiInputBase-input': {
      color: '#F0F0F0',
      backgroundColor: '#404040'
    },
    '& .MuiInputLabel-root': {
      color: '#F0F0F0',
      '&.Mui-focused': {
        color: '#F0F0F0'
      },
      // Conditional color based on disabled state
      '&.Mui-disabled': {
        color: '#B0B0B0'
      }
    },
    '& .MuiOutlinedInput-root': {
      '& fieldset': {
        borderColor: '#00000000'
      },
      '&:hover fieldset': {
        borderColor: '#00000000'
      },
      '&.Mui-focused fieldset': {
        borderColor: '#00000000'
      }
    },
    '& .Mui-disabled': {
      color: '#B0B0B0 !important'
    }
  }
}

interface ParentProps {
  closePopup: (val: boolean) => void
  isOpen: boolean
}
interface LabelValue {
  label: string
  value: string
}

const AddDevice = (props: ParentProps & AllProps): JSX.Element => {
  const [deviceType, setDeviceType] = React.useState('')
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null)
  const [file, setFile] = React.useState<File | null>(null)
  const [protocol, setProtocol] = React.useState<LabelValue | null>(null)
  const [deviceTypeErrorInit, setDeviceTypeErrorInit] = React.useState(false)
  const [showSuccessPopUp, setshowSuccessPopUp] = React.useState(false)
  const [protocolOptions, setProtocolOptions] = React.useState<Option[]>([])
  const [sampleFileUrl, setSampleFileUrl] = React.useState<string>('')
  const createCustomDeviceInfoState = useSelector((state: RootState) => state.createCustomDeviceInfoState)

  useEffect(() => {
    props.getCustomDeviceDropdownMetadata()

    // Sample file will be loaded lazily when user clicks download link
    // No automatic download on component mount

    // Cleanup function to revoke blob URL
    return () => {
      if (sampleFileUrl) {
        window.URL.revokeObjectURL(sampleFileUrl)
      }
    }
  }, [])

  const protocolOptionsData = useSelector((state: RootState) => state.deviceDropDownMetadata.DeviceDropDownMetadata?.protocols)

  React.useEffect(() => {
    if (protocolOptionsData !== undefined && protocolOptionsData.length > 0) {
      const protocolOptionsDataOptions: Option[] = []
      protocolOptionsData?.forEach((item: any) => {
        protocolOptionsDataOptions.push({
          value: item.commTypeId,
          label: item.commType
        })
      })
      setProtocolOptions(protocolOptionsDataOptions)
    }
  }, [protocolOptionsData])

  // Update error message when file or its properties change
  useEffect(() => {
    setErrorMessage(getFirstErrorMessage())
  }, [file]) // Dependency array ensures this runs when `file` changes

  // Function to handle sample file download click
  const handleSampleFileClick = (e: React.MouseEvent<HTMLAnchorElement>): void => {
    if (!sampleFileUrl) {
      e.preventDefault() // Prevent navigation until file is loaded

      // Load sample file lazily when user clicks
      void getSampleDeviceOnboardingFileUrl(
        (fileUrl, httpStatus) => {
          setSampleFileUrl(fileUrl)
          // Create a temporary anchor to trigger download
          const tempLink = document.createElement('a')
          tempLink.href = fileUrl
          tempLink.download = 'SampleDeviceOnboarding.xlsx'
          tempLink.click()
        },
        (errorMessage, httpStatus) => {
          // Error toast is shown by the service
        }
      )
    }
  }

  // Function to determine the first applicable error message
  const getFirstErrorMessage = (): any => {
    if (file === null) return null
    if (file !== null && !isFileSizeLessThan10MB(file.size)) return AddDeviceStrings.errormsg1MB
    if (file !== null && !isValidFileUploadedXl(file)) return AddDeviceStrings.errormsgXL
    if (file !== null && !isValidName(file.name)) return AddDeviceStrings.filenameError
    if (file !== null && !isValidFileNameLength(file)) return AddDeviceStrings.fileNameLengthError
    return null
  }

  const isDeviceTypeNotValid = (): boolean => {
    return (
      !isValidDeviceType(deviceType) || deviceType.length > DEVICE_TYPE_MAX_LENGTH ||
    deviceType.length < 5
    )
  }
  const isProtocolNotValid = (): boolean => protocol === null
  const shouldDisableAddButton = (): boolean => {
    return (isDeviceTypeNotValid() || isProtocolNotValid() || (file === null || errorMessage !== null))
  }
  const isFileSizeLessThan10MB = (fileSize: number): boolean => {
    return Math.round((fileSize / 1024)) < 10240
  }
  const isValidFileUploadedXl = (file: any): boolean => {
    const validExtensions = ['csv', 'xlsx']
    const fileExtension = file.name.split('.').pop()?.toLowerCase()
    return validExtensions.includes(fileExtension)
  }
  const isValidFileNameLength = (file: any): boolean => {
    const fileNameWithoutExtension = file.name.split('.')[0]
    return fileNameWithoutExtension.length <= 20
  }
  const truncateFileName = (name: string): string => {
    const maxLength = 25
    return name.length > maxLength ? name.substring(0, maxLength) + '...' : name
  }
  const handleClose = (event: MouseEvent, reason: 'backdropClick' | 'escapeKeyDown'): void => {
    // istanbul ignore next
    if (reason !== 'backdropClick') props.closePopup(false)
  }
  const handleProtocolSelect = (selectedValue: LabelValue): void => {
    setProtocol(selectedValue)
  }
  const handleDrag = (e: any): void => {
    e.preventDefault()
    e.stopPropagation()
  }
  const handleDragOver = (e: any): void => {
    const event = e as Event
    event.stopPropagation()
    event.preventDefault()
  }
  const handleDrop = (e: any): void => {
    e.preventDefault()
    e.stopPropagation()
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    if (e.dataTransfer?.files && e.dataTransfer?.files[0]) {
      setFile(e.dataTransfer.files[0])
    }
  }
  const onFileChange = (e: any): void => {
    setFile(e.target.files[0])
  }
  const onRemoveSelectedFile = (): void => {
    setFile(null)
  }
  const handleSubmit = (): void => {
    if (file !== null) {
      const deviceTemplateRequest = {
        deviceType,
        commTypeId: protocol?.value
      }
      props.createCustomeDeviceTemplateService(file, deviceTemplateRequest)
    }
    setshowSuccessPopUp(true)
  }

  const handleOkandClose = (): void => {
    // istanbul ignore next
    if (createCustomDeviceInfoState.httpStatus === 200) {
      setshowSuccessPopUp(false)
      props.closePopup(false)
      props.fetchDeviceList(1, 10, '', 'desc', 'modifiedAt')
    } else {
      setshowSuccessPopUp(false)
    }
  }

  const handleDeviceAddnWitFailure = (): JSX.Element => {
    // istanbul ignore next
    if (createCustomDeviceInfoState.isLoading) {
      return (
        <ModelPopup title={'Loading...'}>
          <div className='CircularprogressAmi'>
            <CircularProgress />
          </div>
        </ModelPopup>
      )
    } else if (createCustomDeviceInfoState.httpStatus === 400) {
      return (
        <ModelPopup buttonTypeNegative='hollow'
          title={'Create Device Template'}
          LabelPositive={'Ok'}
          onClickPositive={() => { handleOkandClose() }}
          onClose={() => { handleOkandClose() }}
        >
          <p style={{ paddingTop: '1.2em' }}>
            {createCustomDeviceInfoState.loadingError}
          </p>
        </ModelPopup>
      )
    } else if (createCustomDeviceInfoState.httpStatus === 200) {
      return (
        <ModelPopup buttonTypeNegative='hollow'
          title={'Create Device Template'}
          LabelPositive={'Ok'}
          onClickPositive={() => { handleOkandClose() }}
          onClose={() => { handleOkandClose() }}
        >
          <p style={{ paddingTop: '1.2em' }}>
            {createCustomDeviceInfoState.CreateCustomDevice?.message}
          </p>
        </ModelPopup>
      )
    } else {
      return (
        <ModelPopup
          buttonTypeNegative='hollow'
          title={'Create Device Template'}
          LabelPositive={'Ok'}
          onClickPositive={() => {
            setshowSuccessPopUp(false)
          }}
          onClose={() => {
            setshowSuccessPopUp(false)
          }}
        >
          <p style={{ paddingTop: '1.2em' }}>
            {createCustomDeviceInfoState.loadingError}
          </p>
        </ModelPopup>
      )
    }
  }

  return (
    <>
    <Dialog
        open={props.isOpen}
        onClose={handleClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        sx={{
          '& .MuiDialog-paper': {
            backgroundColor: '#252525', // Set your desired background color
            color: '#f0f0f0'
          },
          zIndex: 999
        }}
        maxWidth="md"
        fullWidth={true}
      >
        <DialogTitle>
           <Grid item sx={{ display: 'flex', justifyContent: 'space-between' }} >
            <Grid item xs={10} >
              <p>{AddDeviceStrings.CreateDeviceTemplate}</p>
            </Grid>
            <Grid item xs={2} sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
              <CloseIcon data-testid="close-icon" className="custom-device-close-icon" onClick={() => { props.closePopup(false) }} />
            </Grid>
        </Grid>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={3} sx={{ display: 'flex' }}>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={3} >
              <span style={{ fontSize: '16px', display: 'block', padding: '10px 3px', color: '#F0F0F0' }}>
                {AddDeviceStrings.DeviceType}
                <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500' }}>*</sup>
              </span>
              <TextField data-testid="device-type" id="device-type-field" variant="outlined" value={deviceType} placeholder="Enter Details"
                InputLabelProps={{
                  disabled: true
                }}
                onChange={(e) => {
                  setDeviceType(e.target.value)
                  setDeviceTypeErrorInit(true)
                }}
                size="small" autoComplete="off"
                className={`custom-textfield ${isDeviceTypeNotValid() && deviceTypeErrorInit ? 'error' : ''}`}
                sx={{ ...textStyle.root, width: '100%' }}
              />
              {isDeviceTypeNotValid() && deviceTypeErrorInit && (
                // istanbul ignore next
                <div style={{ display: 'flex' }}>
                  <div style={{ marginTop: '3px' }}>
                    <img src={errorCrossIcon} alt="Error" />
                  </div>
                  <FormHelperText sx={{ color: '#EE3426', paddingLeft: '5px', width: '85%' }} className="error-msg" >
                    {AddDeviceStrings.deviceTypeError}
                  </FormHelperText>
                </div>
              )}
            </Grid>
            <Grid item xs={12} sm={12} md={6} lg={6} xl={3} >
              <span style={{ fontSize: '16px', display: 'block', padding: '10px 3px', color: '#F0F0F0' }}>
                {AddDeviceStrings.Protocol}
                <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500' }}>*</sup>
              </span>
              <GenXDropdown width={'100%'} placeholder={AddDeviceStrings.SelectProtocol} value={protocol} options={protocolOptions} onSelect={handleProtocolSelect} data-testId="protocol-dropdown" />
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={12} xl={6} >
              <div className="download-file" style={{ display: 'flex', justifyContent: 'space-between', height: '20px' }}>
                <span style={{ fontSize: '16px', display: 'block', color: '#F0F0F0' }}>
                  {AddDeviceStrings.deviceFile}
                  <sup style={{ color: '#B0B0B0', fontSize: '16px', fontStyle: 'normal', fontWeight: '500' }}>*</sup>
                </span>
                <a
                  href={sampleFileUrl || '#'}
                  data-testid="admin_link"
                  target="_blank"
                  download="SampleDeviceOnboarding.xlsx"
                  style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center' }}
                  rel="noreferrer"
                  onClick={handleSampleFileClick}
                >
                  <DescriptionOutlinedIcon style={{ cursor: 'pointer' }} sx={{ color: '#64C3FF', paddingRight: '3px' }} />
                  <span style={{ color: '#64C3FF', cursor: 'pointer' }}>{AddDeviceStrings.DownloadFileFormat}</span>
                </a>
              </div>
              <div className="device-drag-drop-container">
                <Box className="modal-box-device" onDragEnter={handleDrag} onDragOver={handleDragOver} onDrop={handleDrop}>
                  <DescriptionOutlinedIcon className="DescriptionOutlinedIcon" />
                  <Typography className="draganddroptext-device">
                    {AddDeviceStrings.DragAndDropHere} {' '}
                  </Typography>
                  <label className="file-label-device" htmlFor="file-input">
                    <input type="file" id="file-input" className="input-file" data-testid="file-input"
                      onChange={(e: any) => {
                        onFileChange(e)
                      }}
                      onClick={(event: any) => {
                        event.target.value = null
                      }}
                    />
                    {AddDeviceStrings.BrowseForFiles}{' '}
                  </label>
                </Box>
                {file !== null && <div className='upload-file-name'>{truncateFileName(file.name)} ({(file.size / (1024 * 1024)).toFixed(2)}MB / 10MB)  <CloseIcon
                  className='upload-file-close'
                  data-testid='close-selected-file'
                  onClick={() => { onRemoveSelectedFile() }}
                /></div>}
                {errorMessage !== null && (
                  <div className='failure-upload-response'>
                    <WarningIcon className='error-upload-icon' />
                    <span className='error-upload-message'>{errorMessage}</span>
                  </div>
                )}
              </div>
              <Typography className="subtext-device">
                {AddDeviceStrings.fileValidation}
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container sx={{ display: 'flex', justifyContent: 'end' }}>
            <Grid item >
              <Button type='hollow' className="view-close-btn" dataTestId="close-btn-click" onClick={() => { props.closePopup(false) }}>
                {AddDeviceStrings.Cancel}
              </Button>
              <Button dataTestId="add-device-btn" onClick={() => { handleSubmit() }} disabled={shouldDisableAddButton()}
                className={
                  `${shouldDisableAddButton() ? 'btn-disabled' : ''}`
                }
              >{AddDeviceStrings.Add}
              </Button>
            </Grid>
        </Grid>
        { showSuccessPopUp &&
          handleDeviceAddnWitFailure()
        }
        </DialogActions>
      </Dialog>
    </>
  )
}

interface DispatchToProps {
  createCustomeDeviceTemplateService: (file: File, deviceTemplateRequest: any) => void
  getCustomDeviceDropdownMetadata: () => void
  fetchDeviceList: (PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string) => void

}
const mapDispatchToProps = (dispatch: any): DispatchToProps => ({
  fetchDeviceList: (PageNumber: number, PageItems: number, searchKey: string, SortParams: string, sortField: string) => dispatch(fetchDeviceList(PageNumber, PageItems, searchKey, SortParams, sortField)),
  createCustomeDeviceTemplateService: (file: File, deviceTemplateRequest: any) => dispatch(createCustomeDeviceTemplateService(file, deviceTemplateRequest)),
  getCustomDeviceDropdownMetadata: () => dispatch(getCustomDeviceDropdownMetadata())
})
interface StateToProps {
  createCustomDeviceInfoState: UploadFileInfoState
  DeviceDropDownMetadata: DeviceDropDownMetadataState
}
const mapStateToProps = (state: RootState): StateToProps => ({
  createCustomDeviceInfoState: state.createCustomDeviceInfoState,
  DeviceDropDownMetadata: state.deviceDropDownMetadata
})

const connector = connect(mapStateToProps, mapDispatchToProps)
type PropsFromRedux = ConnectedProps<typeof connector>

type AllProps = PropsFromRedux & ParentProps
export default connector(AddDevice)
